/**
 * BulkSelectionBar — floating bottom bar shown when team members are selected.
 *
 * Mirrors the design in the screenshot: teal background, selected count,
 * outstanding/overdue totals, Export + Actions dropdowns, and a Clear button.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Users,
  ChevronDown,
  X,
  Building2,
  GitBranch,
} from 'lucide-react';

interface BulkSelectionBarProps {
  selectedCount: number;
  onClear: () => void;
  onBulkUpdate: () => void;
}

export const BulkSelectionBar: React.FC<BulkSelectionBarProps> = ({
  selectedCount,
  onClear,
  onBulkUpdate,
}) => {
  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1a7f6e] text-white shadow-2xl text-sm whitespace-nowrap">
        {/* Count badge */}
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/25 text-xs font-bold">
          {selectedCount}
        </span>
        <span className="font-medium pr-1">
          {selectedCount === 1 ? 'member' : 'members'} selected
        </span>

        <div className="h-4 w-px bg-white/30" />

        {/* Actions dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2.5 text-white hover:bg-white/20 hover:text-white focus-visible:ring-white/40 font-medium"
            >
              Actions
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" side="top" className="w-52 mb-2">
            <DropdownMenuItem onClick={onBulkUpdate} className="gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              Update Company Access
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onBulkUpdate} className="gap-2">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
              Update Approval Workflow
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onClear} className="gap-2 text-muted-foreground">
              <X className="h-4 w-4" />
              Clear Selection
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="h-4 w-px bg-white/30" />

        {/* Clear */}
        <button
          onClick={onClear}
          className="flex items-center gap-1 text-white/80 hover:text-white transition-colors px-1"
          aria-label="Clear selection"
        >
          <X className="h-3.5 w-3.5" />
          <span className="text-xs">Clear</span>
        </button>
      </div>
    </div>
  );
};
